import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { endpoints } from "@/api";
import { useAuth } from "@/auth";
import { Button, Card } from "@/components/primitives";
import logo from "@/assets/c4b-logo.png";

export default function SetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [token, setToken] = useState(sp.get("token") || "");
  const [tokenInput, setTokenInput] = useState("");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const loadToken = (t) => {
    setErr("");
    endpoints.setupInfo(t).then((i) => { setInfo(i); setToken(t); })
      .catch((e) => setErr(e?.response?.data?.detail || "This setup link is invalid or expired."));
  };

  useEffect(() => { if (token) loadToken(token); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 12) { setErr("Password must be at least 12 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      const data = await endpoints.setPassword({ token, password: pw });
      setSession(data);
      nav("/");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not set password.");
    } finally { setBusy(false); }
  };

  const inputStyle = { width: "100%", background: "var(--ui-bg)", border: "1px solid var(--ui-line)",
    borderRadius: 10, padding: "12px 14px", color: "var(--ui-text-hi)", fontFamily: "'Inter', sans-serif", fontSize: 14, marginTop: 6 };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--ui-bg)" }}>
      <Card style={{ width: 420, padding: 34 }}>
        <img src={logo} alt="coffee4business" style={{ width: 200, maxWidth: "100%", height: "auto", objectFit: "contain", display: "block", marginInline: "auto", marginBottom: 24 }} />
        <div className="num" style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.6px", color: "var(--ui-text-hi)" }}>Set your password</div>

        {/* Step 1: no valid token yet — let the user paste their setup code */}
        {!info && (
          <>
            <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)", marginTop: 4, marginBottom: 18 }}>
              Paste the setup code your administrator gave you (from the setup link).
            </div>
            <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>Setup code
              <input data-testid="setup-token-input" style={inputStyle} value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value.trim())} placeholder="e.g. uHsIkXFd5-…" />
            </label>
            {err && <div className="prose-i" style={{ color: "var(--sv-crit)", fontSize: 12.5, marginTop: 12 }} data-testid="setpw-error">{err}</div>}
            <Button data-testid="setup-token-continue" type="button" style={{ width: "100%", marginTop: 20 }}
              disabled={!tokenInput} onClick={() => loadToken(tokenInput)}>Continue</Button>
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <a href="/login" className="prose-i" style={{ fontSize: 12.5, color: "var(--ac-indigo-hi)", textDecoration: "none" }}>← Back to sign in</a>
            </div>
          </>
        )}

        {/* Step 2: token resolved — set the password */}
        {info && (
          <>
            <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)", marginTop: 4, marginBottom: 20 }} data-testid="setpw-email">
              {info.email} · {info.role}
            </div>
            <form onSubmit={submit}>
              <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>New password (min 12 characters)
                <input data-testid="setpw-password" type="password" style={inputStyle} value={pw} onChange={(e) => setPw(e.target.value)} />
              </label>
              <div style={{ height: 14 }} />
              <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>Confirm password
                <input data-testid="setpw-confirm" type="password" style={inputStyle} value={pw2} onChange={(e) => setPw2(e.target.value)} />
              </label>
              {err && <div className="prose-i" style={{ color: "var(--sv-crit)", fontSize: 12.5, marginTop: 12 }} data-testid="setpw-error">{err}</div>}
              <Button data-testid="setpw-submit" type="submit" style={{ width: "100%", marginTop: 22 }} disabled={busy}>
                {busy ? "Saving…" : "Set password & sign in"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
